import React, { useState, useEffect, useRef } from "react";
import ReactPaginate from "react-paginate";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  User,
  Mail,
  Shield,
  Lock,
  UserPlus,
  Users,
  Edit2,
  X,
  Check,
  AlertCircle,
  Trash2,
  KeyRound,
  PanelRightOpen,
} from "lucide-react";
import SearchBar from "./SearchBar";
import UserDetails from "./UserDetails";
import { fetchRoles } from "../apis/role";
import { fetchDepartments } from "../apis/department";
import { displayToast } from "../utils/displayToast";
import { useErrorHandler } from "../utils/ErrorHandler";
import {
  createUsers,
  fetchUsers,
  fetchUserOnlineStatus,
  editUsers,
  deleteUsers,
  resetPassword,
} from "../apis/users";
import "../styles/components/UserManagement.css";
import "../styles/components/PageStyles.css";

const CustomSelect = ({ options, value, onChange, placeholder }) => {
  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const selectedLabel = options.find((o) => o.value === value)?.label || "";

  useEffect(() => {
    function handleOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    const wrap = wrapperRef.current;
    const list = listRef.current;
    if (!wrap || !list) return;

    const rect = wrap.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;

    const margin = 12;
    const availableBelow = Math.max(0, spaceBelow - margin);
    const availableAbove = Math.max(0, spaceAbove - margin);

    if (availableBelow < 200 && availableAbove > availableBelow) {
      setOpenUp(true);
      list.style.maxHeight = `${Math.min(availableAbove, 360)}px`;
    } else {
      setOpenUp(false);
      list.style.maxHeight = `${Math.min(
        availableBelow || viewportH - rect.top - 80,
        360,
      )}px`;
    }

    setTimeout(() => {
      list && (list.style.willChange = "transform");
    }, 10);
  }, [open]);

  const handleToggle = () => setOpen((v) => !v);

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
  };

  return (
    <div className="cm-select-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className={`cm-select-toggle ${open ? "open" : ""}`}
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className={`cm-select-value ${!selectedLabel ? "placeholder" : ""}`}
        >
          {selectedLabel || placeholder}
        </span>
        <span className="cm-select-caret" />
      </button>

      <ul
        ref={listRef}
        className={`cm-select-list ${open ? "visible" : ""} ${
          openUp ? "open-up" : "open-down"
        }`}
        role="listbox"
        tabIndex={-1}
      >
        {options.map((opt) => (
          <li
            key={opt.value || opt.label}
            role="option"
            aria-selected={value === opt.value}
            className={`cm-select-option ${
              value === opt.value ? "selected" : ""
            }`}
            onClick={() => handleSelect(opt.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleSelect(opt.value);
              }
            }}
            tabIndex={0}
          >
            {opt.label}
          </li>
        ))}
      </ul>
    </div>
  );
};

const UserManagement = () => {
  const { handleErrorResponse } = useErrorHandler();
  const itemsPerPage = 8;
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    department_id: "",
    group: "",
    password: "",
  });
  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isResetPasswordLoading, setIsResetPasswordLoading] = useState(false);
  const [isUsetDataLoading, setIsUsetDataLoading] = useState(false);
  const [currentFilters, setCurrentFilters] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isAddUserSubmitted, setIsAddUserSubmitted] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [resetPasswordData, setResetPasswordData] = useState({
    new_password: "",
    confirm_password: "",
  });
  const [showResetNew, setShowResetNew] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const getInitials = (name) => {
    if (!name) return "??";
    const parts = name.split(/[\s_.-]+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (name) => {
    if (!name) return "#94a3b8";
    const colors = [
      "#4a90d9",
      "#059669",
      "#d97706",
      "#dc2626",
      "#8b5cf6",
      "#0284c7",
      "#10b981",
      "#f97316",
      "#f43f5e",
      "#14b8a6",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const handlePageClick = ({ selected }) => setCurrentPage(selected);

  const handleOpenUserDetails = (user) => {
    setViewingUser(user);
  };

  const closeViewModal = () => {
    setViewingUser(null);
  };

  const formatActivity = (value) => {
    if (!value) return "Never";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
    return date.toLocaleDateString();
  };

  const formatLogin = (value) => {
    if (!value) return "Never";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const today = new Date();
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    const dayDifference = Math.round((todayOnly - dateOnly) / 86400000);
    const time = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    if (dayDifference === 0) return `Today, ${time}`;
    if (dayDifference === 1) return `Yesterday, ${time}`;
    return `${date.toLocaleDateString([], { day: "numeric", month: "short" })}, ${time}`;
  };

  const getDepartmentName = (user) => {
    const departmentId = user.department_id || user.user_role?.department_id;
    return (
      user.department ||
      user.department_name ||
      user.user_role?.department ||
      departments.find(
        (department) => String(department.id) === String(departmentId),
      )?.name ||
      "N/A"
    );
  };

  const closeDeleteModal = () => {
    setUserToDelete(null);
  };

  const getStatusBadgeClass = (status) => {
    const statusLower = String(status).toLowerCase();
    if (statusLower === "active") return "mfg-status-active";
    if (statusLower === "inactive") return "mfg-status-inactive";
    if (statusLower === "pending") return "mfg-status-pending";
    return "mfg-status-default";
  };

  useEffect(() => {
    const fetchRoleAndDepartmentData = async () => {
      try {
        const [rolesResponse, departmentsResponse] = await Promise.all([
          fetchRoles(),
          fetchDepartments(),
        ]);
        if (rolesResponse?.description) {
          handleErrorResponse(rolesResponse.description);
          return;
        }
        if (departmentsResponse?.description) {
          handleErrorResponse(departmentsResponse.description);
          return;
        }
        setRoles(rolesResponse || []);
        setDepartments(departmentsResponse || []);
      } catch (error) {
        handleErrorResponse("Failed to fetch roles and departments");
        console.error(error);
      }
    };

    fetchRoleAndDepartmentData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsersData = async (filters = {}) => {
    try {
      setIsUsetDataLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.append("count", itemsPerPage);
      queryParams.append("page", currentPage + 1);

      if (filters.user_name) {
        queryParams.append("user_name", filters.user_name);
      }

      const query = `?${queryParams.toString()}`;
      const response = await fetchUsers(query);

      if (response.description) {
        handleErrorResponse(response.description);
        return;
      }
      let statusItems = [];
      try {
        const statusResponse = await fetchUserOnlineStatus();
        statusItems =
          statusResponse?.items ||
          statusResponse?.users ||
          (Array.isArray(statusResponse) ? statusResponse : []);
      } catch (statusError) {
        console.error("Failed to fetch user online status", statusError);
      }
      const statusByUserId = new Map(
        statusItems.map((item) => [String(item.user_id || item.id), item]),
      );
      setUsers(
        (response.items || []).map((user) => ({
          ...user,
          ...(statusByUserId.get(String(user.id)) || {}),
        })),
      );
      if (response.total_count) {
        setPageCount(Math.ceil(response.total_count / itemsPerPage));
      }

      setCurrentFilters(filters);
    } catch (error) {
      handleErrorResponse("Failed to fetch users");
      console.error(error);
    } finally {
      setIsUsetDataLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersData(currentFilters);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (searchText) => {
    const trimmedText = searchText.trim();
    setCurrentPage(0);

    if (!trimmedText) {
      fetchUsersData();
    } else {
      fetchUsersData({
        user_name: trimmedText,
      });
    }
  };

  const handleAddUser = async () => {
    setIsAddUserSubmitted(true);

    if (newUser.password.length < 8) {
      displayToast("Password length should be greater than 8", "error");
      return;
    }

    if (!newUser.username || !newUser.department_id || !newUser.group) {
      displayToast("Username, department and role are required", "error");
      return;
    }

    const selectedRole = roles.find(
      (role) =>
        role.name === newUser.group &&
        String(role.department_id) === String(newUser.department_id),
    );
    if (!selectedRole) {
      displayToast("Invalid role selected", "error");
      return;
    }

    const userData = {
      email: newUser.email,
      user_name: newUser.username,
      password: newUser.password,
      role: newUser.group,
      role_id: selectedRole.id,
    };

    try {
      setIsLoading(true);
      const response = await createUsers(userData);

      if (response?.id) {
        displayToast("User created successfully", "success");
        const newUserObj = {
          id: response.id,
          user_name: newUser.username,
          email: newUser.email,
          user_role: {
            role: newUser.group,
            role_id: selectedRole.id,
          },
          status: response.status || "Active",
        };

        if (currentPage === 0 && !currentFilters.user_name) {
          setUsers((prevUsers) => {
            const updatedUsers = [newUserObj, ...prevUsers];
            return updatedUsers.slice(0, itemsPerPage);
          });

          setPageCount((prevCount) => {
            const totalItems =
              (prevCount - 1) * itemsPerPage + users.length + 1;
            return Math.ceil(totalItems / itemsPerPage);
          });
        } else {
          await fetchUsersData(currentFilters);
        }

        setNewUser({
          username: "",
          email: "",
          department_id: "",
          group: "",
          password: "",
        });
        setIsAddUserSubmitted(false);
      } else {
        displayToast(response?.description || "Failed to create user", "error");
      }
    } catch (error) {
      displayToast("An error occurred while creating the user", "error");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditUser = (user) => {
    const selectedRole = roles.find(
      (role) => role.id === user.user_role?.role_id,
    );
    setEditingUser({
      id: user.id,
      username: user.user_name,
      email: user.email,
      department_id:
        selectedRole?.department_id ||
        user.department_id ||
        user.department?.id ||
        user.user_role?.department_id ||
        "",
      group: user.user_role?.role || "",
      role_id: user.user_role?.role_id || "",
    });
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setIsDeleteLoading(true);
      const response = await deleteUsers(userToDelete.id);

      if (response && !response.description) {
        displayToast("User deleted successfully", "success");
        closeDeleteModal();

        if (users.length === 1 && currentPage > 0) {
          setCurrentPage((prevPage) => prevPage - 1);
        } else {
          await fetchUsersData(currentFilters);
        }
      } else {
        displayToast(response?.description || "Failed to delete user", "error");
      }
    } catch (error) {
      displayToast("An error occurred while deleting the user", "error");
      console.error(error);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const openResetPasswordModal = (user = editingUser) => {
    if (!user) return;
    setResetPasswordUser({
      ...user,
      username: user.username || user.user_name,
    });
    setResetPasswordData({ new_password: "", confirm_password: "" });
    setShowResetNew(false);
    setShowResetConfirm(false);
  };

  const closeResetPasswordModal = () => {
    setResetPasswordUser(null);
    setResetPasswordData({ new_password: "", confirm_password: "" });
  };

  const handleResetPasswordSubmit = async () => {
    if (!resetPasswordUser) return;
    if (resetPasswordData.new_password.length < 8) {
      displayToast("New password must be at least 8 characters", "error");
      return;
    }
    if (resetPasswordData.new_password !== resetPasswordData.confirm_password) {
      displayToast("New passwords do not match", "error");
      return;
    }
    try {
      setIsResetPasswordLoading(true);
      const response = await resetPassword(resetPasswordUser.id, {
        new_password: resetPasswordData.new_password,
      });
      if (response && !response.description) {
        displayToast("Password reset successfully", "success");
        closeResetPasswordModal();
      } else {
        displayToast(
          response?.description || "Failed to reset password",
          "error",
        );
      }
    } catch (error) {
      displayToast("An error occurred while resetting the password", "error");
      console.error(error);
    } finally {
      setIsResetPasswordLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    if (!editingUser.department_id || !editingUser.group) {
      displayToast("Department and role are required", "error");
      return;
    }

    const selectedRole = roles.find(
      (role) =>
        role.name === editingUser.group &&
        String(role.department_id) === String(editingUser.department_id),
    );
    if (!selectedRole) {
      displayToast("Invalid role selected", "error");
      return;
    }

    const userData = {
      id: editingUser.id,
      email: editingUser.email,
      role: editingUser.group,
      role_id: selectedRole.id,
    };

    try {
      setIsEditLoading(true);
      const response = await editUsers(userData);

      if (response && !response.description) {
        displayToast("User updated successfully", "success");

        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === editingUser.id
              ? {
                  ...user,
                  email: editingUser.email,
                  user_role: {
                    role: editingUser.group,
                    role_id: selectedRole.id,
                  },
                }
              : user,
          ),
        );

        setViewingUser((current) =>
          current?.id === editingUser.id
            ? {
                ...current,
                email: editingUser.email,
                user_role: {
                  ...current.user_role,
                  role: editingUser.group,
                  role_id: selectedRole.id,
                },
              }
            : current,
        );

        setEditingUser(null);
      } else {
        displayToast(response?.description || "Failed to update user", "error");
      }
    } catch (error) {
      displayToast("An error occurred while updating the user", "error");
      console.error(error);
    } finally {
      setIsEditLoading(false);
    }
  };

  const getRolesForDepartment = (departmentId) =>
    roles.filter((role) => String(role.department_id) === String(departmentId));

  const roleOptions = getRolesForDepartment(newUser.department_id).map(
    (role) => ({
      value: role.name,
      label: role.name,
    }),
  );
  const editRoleOptions = getRolesForDepartment(editingUser?.department_id).map(
    (role) => ({ value: role.name, label: role.name }),
  );

  return (
    <div className="mfg-page-container user-management-enhanced">
      <div className="mfg-page-header">
        <div className="mfg-page-title-section">
          <h2 className="mfg-page-title">
            <Users className="mfg-page-title-icon-svg" size={28} />
            User Management
          </h2>
          <p className="mfg-page-subtitle">
            Manage system users and their roles
          </p>
        </div>
        <div className="mfg-header-stats">
          <div className="mfg-stat-badge">
            <span className="mfg-stat-number">{users.length}</span>
            <span className="mfg-stat-label">Users on page</span>
          </div>
        </div>
      </div>

      <div className="mfg-card mfg-card-elevated">
        <div className="mfg-card-header">
          <h3 className="mfg-card-title">
            <UserPlus className="mfg-card-title-icon-svg" size={20} />
            Add New User
          </h3>
        </div>
        <div className="mfg-card-body">
          <form
            className="mfg-form-grid mfg-form-enhanced"
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              handleAddUser();
            }}
          >
            <div className="mfg-form-group">
              <label className="mfg-form-label">
                <User size={14} className="mfg-label-icon" />
                Username
              </label>
              <div className="mfg-input-wrapper">
                <input
                  type="text"
                  placeholder="Enter username"
                  className={`mfg-form-input mfg-input-with-icon ${isAddUserSubmitted && !newUser.username ? "mfg-input-error" : ""}`}
                  value={newUser.username}
                  autoComplete="off"
                  onChange={(e) =>
                    setNewUser({ ...newUser, username: e.target.value })
                  }
                  required
                />
                {isAddUserSubmitted && !newUser.username && (
                  <span className="mfg-input-error-msg">Required</span>
                )}
              </div>
              <div className="mfg-password-hint"></div>
            </div>
            <div className="mfg-form-group">
              <label className="mfg-form-label">
                <Mail size={14} className="mfg-label-icon" />
                Email
              </label>
              <div className="mfg-input-wrapper">
                <input
                  type="email"
                  placeholder="Enter email"
                  className="mfg-form-input mfg-input-with-icon"
                  value={newUser.email}
                  autoComplete="off"
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                  required
                />
              </div>
              <div className="mfg-password-hint"></div>
            </div>
            <div className="mfg-form-group">
              <label className="mfg-form-label">
                <Shield size={14} className="mfg-label-icon" />
                Department
              </label>
              <div className="user-management-custom-dropdown">
                <CustomSelect
                  options={[
                    { value: "", label: "Select Department" },
                    ...departments.map((department) => ({
                      value: String(department.id),
                      label: department.name,
                    })),
                  ]}
                  value={newUser.department_id}
                  onChange={(val) =>
                    setNewUser({ ...newUser, department_id: val, group: "" })
                  }
                  placeholder="Select Department"
                />
              </div>
              <div className="mfg-password-hint"></div>
            </div>
            <div className="mfg-form-group">
              <label className="mfg-form-label">
                <Shield size={14} className="mfg-label-icon" />
                Role
              </label>
              <div className="user-management-custom-dropdown">
                <CustomSelect
                  options={[
                    { value: "", label: "Select Role" },
                    ...roleOptions,
                  ]}
                  value={newUser.group}
                  onChange={(val) => setNewUser({ ...newUser, group: val })}
                  placeholder={
                    newUser.department_id
                      ? "Select Role"
                      : "Select Department First"
                  }
                />
              </div>
              <div className="mfg-password-hint"></div>
            </div>
            <div className="mfg-form-group">
              <label className="mfg-form-label">
                <Lock size={14} className="mfg-label-icon" />
                Password
              </label>
              <div className="mfg-input-wrapper mfg-password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password (min 8 chars)"
                  className={`mfg-form-input mfg-input-with-icon ${newUser.password.length > 0 && newUser.password.length < 8 ? "mfg-input-warning" : ""}`}
                  value={newUser.password}
                  autoComplete="new-password"
                  onChange={(e) =>
                    setNewUser({ ...newUser, password: e.target.value })
                  }
                  required
                />
                <button
                  type="button"
                  className="mfg-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <div className="mfg-password-hint">
                {newUser.password.length > 0 && newUser.password.length < 8 && (
                  <span className="mfg-password-strength mfg-strength-weak">
                    <AlertCircle size={12} /> Min 8 characters
                  </span>
                )}
                {newUser.password.length >= 8 && (
                  <span className="mfg-password-strength mfg-strength-good">
                    <Check size={12} /> Good length
                  </span>
                )}
              </div>
            </div>
            <div className="mfg-form-actions mfg-form-actions-full">
              <button
                type="submit"
                className="mfg-btn mfg-btn-primary mfg-btn-with-icon"
                disabled={
                  isLoading ||
                  !newUser.username ||
                  !newUser.department_id ||
                  !newUser.group ||
                  newUser.password.length < 8
                }
              >
                {isLoading ? (
                  <span className="mfg-loader-spinner"></span>
                ) : (
                  <>
                    <UserPlus size={18} />
                    Add User
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="mfg-card">
        <div className="mfg-card-header user-management-list-header">
          <h3 className="mfg-card-title user-management-list-title">
            <Users className="mfg-card-title-icon-svg" size={20} />
            Users List
          </h3>
          <SearchBar
            onSearch={handleSearch}
            containerStyles={{ width: "100%" }}
          />
        </div>

        {isUsetDataLoading ? (
          <div className="mfg-loading-state mfg-loading-enhanced">
            <div className="mfg-loading-spinner"></div>
            <div className="mfg-loading-text">
              <span>Loading users</span>
              <span className="mfg-loading-dots"></span>
            </div>
          </div>
        ) : users.length > 0 ? (
          <div className="mfg-table-wrapper mfg-table-enhanced">
            <table className="mfg-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role / Department</th>
                  <th>Account</th>
                  <th>Online Status</th>
                  <th>Sessions</th>
                  <th>Last Activity</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr
                    key={user.id}
                    className="mfg-table-row-animated"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <td data-label="User">
                      <div className="mfg-user-cell">
                        <div
                          className="mfg-user-avatar"
                          style={{
                            backgroundColor: getAvatarColor(user.user_name),
                          }}
                        >
                          {getInitials(user.user_name)}
                        </div>
                        <div className="mfg-user-identity">
                          <span className="mfg-user-name">
                            {user.user_name}
                          </span>
                          <span className="mfg-email-cell">{user.email}</span>
                        </div>
                      </div>
                    </td>
                    <td data-label="Role / Department">
                      <div className="mfg-role-department">
                        <span>
                          {user.user_role?.role || user.role || "N/A"}
                        </span>
                        <small>{getDepartmentName(user)}</small>
                      </div>
                    </td>
                    <td data-label="Account">
                      <span
                        className={`mfg-status-badge mfg-status-animated ${getStatusBadgeClass(user.status)}`}
                      >
                        <span className="mfg-status-dot"></span>
                        {user.status || "N/A"}
                      </span>
                    </td>
                    <td data-label="Online Status">
                      <span
                        className={`mfg-presence-badge ${String(user.online_status).toLowerCase() === "online" || user.online_status === true ? "is-online" : "is-offline"}`}
                      >
                        <span className="mfg-presence-dot" />
                        {user.online_status === true
                          ? "Online"
                          : user.online_status || "Offline"}
                      </span>
                    </td>
                    <td data-label="Sessions" className="mfg-number-cell">
                      {user.active_session_count ?? user.sessions ?? 0}
                    </td>
                    <td data-label="Last Activity">
                      {formatActivity(
                        user.last_activity_at || user.last_active,
                      )}
                    </td>
                    <td data-label="Last Login">
                      {formatLogin(
                        user.last_login_at ||
                          user.last_login ||
                          user.last_activity_at ||
                          user.last_active,
                      )}
                    </td>
                    <td data-label="Actions">
                      <div className="mfg-table-actions">
                        <button
                          onClick={() => handleOpenUserDetails(user)}
                          className="mfg-btn mfg-btn-ghost mfg-btn-sm mfg-btn-icon-only"
                          title="View user details"
                        >
                          <PanelRightOpen size={16} />
                        </button>
                        <button
                          onClick={() => setUserToDelete(user)}
                          className="mfg-btn mfg-btn-ghost-danger mfg-btn-sm mfg-btn-icon-only"
                          title="Delete user"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mfg-empty-state mfg-empty-enhanced">
            <div className="mfg-empty-illustration">
              <Users size={48} strokeWidth={1} />
            </div>
            <h4 className="mfg-empty-title">No Users Found</h4>
            <p className="mfg-empty-text">
              Add a new user to get started or adjust your search filters
            </p>
          </div>
        )}

        {pageCount > 0 && !isUsetDataLoading && (
          <ReactPaginate
            breakLabel="..."
            previousLabel={<ChevronLeft size={20} />}
            nextLabel={<ChevronRight size={20} />}
            pageCount={pageCount}
            forcePage={currentPage}
            onPageChange={handlePageClick}
            breakClassName="pagination-box"
            containerClassName="pagination"
            pageClassName="pagination-box"
            pageLinkClassName="pagination-link"
            previousClassName="pagination-box"
            nextClassName="pagination-box"
            previousLinkClassName="pagination-link"
            nextLinkClassName="pagination-link"
            activeClassName="pagination-active"
            disabledClassName="pagination-disabled"
            breakLinkClassName="pagination-link"
          />
        )}
      </div>

      {viewingUser && (
        <div className="user-details-drawer-overlay" onClick={closeViewModal}>
          <aside
            className="user-details-drawer"
            onClick={(event) => event.stopPropagation()}
          >
            <UserDetails
              userId={viewingUser.id}
              initialUser={viewingUser}
              departments={departments}
              onClose={closeViewModal}
              onEdit={handleEditUser}
              onResetPassword={openResetPasswordModal}
            />
          </aside>
        </div>
      )}

      {editingUser && (
        <div
          className="mfg-modal-overlay mfg-modal-overlay-animated user-edit-overlay"
          onClick={() => setEditingUser(null)}
        >
          <div
            className="mfg-modal mfg-modal-enhanced"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mfg-modal-header">
              <h3 className="mfg-modal-title">
                <Edit2 size={20} /> Edit User
              </h3>
              <button
                className="mfg-modal-close"
                onClick={() => setEditingUser(null)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="mfg-modal-body">
              <div className="mfg-edit-user-header">
                <div
                  className="mfg-profile-avatar-medium"
                  style={{
                    backgroundColor: getAvatarColor(editingUser.username),
                  }}
                >
                  {getInitials(editingUser.username)}
                </div>
                <span className="mfg-edit-username">
                  {editingUser.username}
                </span>
              </div>

              <div className="mfg-form-group">
                <label className="mfg-form-label">
                  <User size={14} className="mfg-label-icon" />
                  Username
                </label>
                <input
                  type="text"
                  placeholder="Username"
                  className="mfg-form-input mfg-input-disabled"
                  value={editingUser.username}
                  onChange={(e) =>
                    setEditingUser({ ...editingUser, username: e.target.value })
                  }
                  disabled
                />
                <span className="mfg-input-hint">
                  Username cannot be changed
                </span>
              </div>

              <div className="mfg-form-group">
                <label className="mfg-form-label">
                  <Mail size={14} className="mfg-label-icon" />
                  Email
                </label>
                <input
                  type="email"
                  placeholder="Email"
                  className="mfg-form-input"
                  value={editingUser.email}
                  onChange={(e) =>
                    setEditingUser({ ...editingUser, email: e.target.value })
                  }
                />
              </div>

              <div className="mfg-form-group">
                <label className="mfg-form-label">
                  <Shield size={14} className="mfg-label-icon" />
                  Department
                </label>
                <CustomSelect
                  options={[
                    { value: "", label: "Select Department" },
                    ...departments.map((department) => ({
                      value: String(department.id),
                      label: department.name,
                    })),
                  ]}
                  value={editingUser.department_id}
                  onChange={(val) =>
                    setEditingUser({
                      ...editingUser,
                      department_id: val,
                      group: "",
                    })
                  }
                  placeholder="Select Department"
                />
              </div>

              <div className="mfg-form-group">
                <label className="mfg-form-label">
                  <Shield size={14} className="mfg-label-icon" />
                  Role
                </label>
                <CustomSelect
                  options={[
                    { value: "", label: "Select Role" },
                    ...editRoleOptions,
                  ]}
                  value={editingUser.group}
                  onChange={(val) =>
                    setEditingUser({ ...editingUser, group: val })
                  }
                  placeholder={
                    editingUser.department_id
                      ? "Select Role"
                      : "Select Department First"
                  }
                />
              </div>
            </div>
            <div className="mfg-modal-footer">
              <button
                onClick={() => setEditingUser(null)}
                className="mfg-btn mfg-btn-outline"
                disabled={isEditLoading || isResetPasswordLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateUser}
                className="mfg-btn mfg-btn-primary mfg-btn-with-icon"
                disabled={isEditLoading || isResetPasswordLoading}
              >
                {isEditLoading ? (
                  <span className="mfg-loader-spinner"></span>
                ) : (
                  <>
                    <Check size={16} /> Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {userToDelete && (
        <div
          className="mfg-modal-overlay mfg-modal-overlay-animated"
          onClick={closeDeleteModal}
        >
          <div
            className="mfg-modal mfg-modal-enhanced"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mfg-modal-header">
              <h3 className="mfg-modal-title">
                <Trash2 size={20} /> Confirm Delete
              </h3>
              <button className="mfg-modal-close" onClick={closeDeleteModal}>
                <X size={18} />
              </button>
            </div>
            <div className="mfg-modal-body">
              <p className="mfg-modal-confirm-text">
                Are you sure you want to delete {userToDelete.user_name}?
              </p>
              <p className="mfg-modal-danger-text">
                This action cannot be undone.
              </p>
            </div>
            <div className="mfg-modal-footer">
              <button
                className="mfg-btn mfg-btn-outline"
                onClick={closeDeleteModal}
                disabled={isDeleteLoading}
              >
                Cancel
              </button>
              <button
                className="mfg-btn mfg-btn-danger mfg-btn-with-icon"
                onClick={handleDeleteUser}
                disabled={isDeleteLoading}
              >
                {isDeleteLoading ? (
                  <span className="mfg-loader-spinner"></span>
                ) : (
                  <>
                    <Trash2 size={16} /> Delete User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetPasswordUser && (
        <div
          className="mfg-modal-overlay mfg-modal-overlay-animated user-reset-password-overlay"
          onClick={closeResetPasswordModal}
        >
          <div
            className="mfg-modal mfg-modal-enhanced"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mfg-modal-header">
              <h3 className="mfg-modal-title">
                <KeyRound size={20} /> Reset Password
              </h3>
              <button
                className="mfg-modal-close"
                onClick={closeResetPasswordModal}
              >
                <X size={18} />
              </button>
            </div>
            <div className="mfg-modal-body">
              <div className="mfg-edit-user-header">
                <div
                  className="mfg-profile-avatar-medium"
                  style={{
                    backgroundColor: getAvatarColor(resetPasswordUser.username),
                  }}
                >
                  {getInitials(resetPasswordUser.username)}
                </div>
                <span className="mfg-edit-username">
                  {resetPasswordUser.username}
                </span>
              </div>

              <div className="mfg-form-group">
                <label className="mfg-form-label">
                  <Lock size={14} className="mfg-label-icon" />
                  New Password
                </label>
                <div className="mfg-input-wrapper mfg-password-wrapper">
                  <input
                    type={showResetNew ? "text" : "password"}
                    placeholder="Enter new password (min 8 chars)"
                    className={`mfg-form-input mfg-input-with-icon ${resetPasswordData.new_password.length > 0 && resetPasswordData.new_password.length < 8 ? "mfg-input-warning" : ""}`}
                    value={resetPasswordData.new_password}
                    autoComplete="new-password"
                    onChange={(e) =>
                      setResetPasswordData({
                        ...resetPasswordData,
                        new_password: e.target.value,
                      })
                    }
                  />
                  <button
                    type="button"
                    className="mfg-password-toggle"
                    onClick={() => setShowResetNew(!showResetNew)}
                    tabIndex={-1}
                  >
                    {showResetNew ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {resetPasswordData.new_password.length > 0 &&
                  resetPasswordData.new_password.length < 8 && (
                    <span className="mfg-password-strength mfg-strength-weak">
                      <AlertCircle size={12} /> Min 8 characters
                    </span>
                  )}
                {resetPasswordData.new_password.length >= 8 && (
                  <span className="mfg-password-strength mfg-strength-good">
                    <Check size={12} /> Good length
                  </span>
                )}
              </div>

              <div className="mfg-form-group">
                <label className="mfg-form-label">
                  <Lock size={14} className="mfg-label-icon" />
                  Confirm New Password
                </label>
                <div className="mfg-input-wrapper mfg-password-wrapper">
                  <input
                    type={showResetConfirm ? "text" : "password"}
                    placeholder="Confirm new password"
                    className={`mfg-form-input mfg-input-with-icon ${resetPasswordData.confirm_password.length > 0 && resetPasswordData.confirm_password !== resetPasswordData.new_password ? "mfg-input-error" : ""}`}
                    value={resetPasswordData.confirm_password}
                    autoComplete="new-password"
                    onChange={(e) =>
                      setResetPasswordData({
                        ...resetPasswordData,
                        confirm_password: e.target.value,
                      })
                    }
                  />
                  <button
                    type="button"
                    className="mfg-password-toggle"
                    onClick={() => setShowResetConfirm(!showResetConfirm)}
                    tabIndex={-1}
                  >
                    {showResetConfirm ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
                {resetPasswordData.confirm_password.length > 0 &&
                  resetPasswordData.confirm_password !==
                    resetPasswordData.new_password && (
                    <span className="mfg-password-strength mfg-strength-weak">
                      <AlertCircle size={12} /> Passwords do not match
                    </span>
                  )}
                {resetPasswordData.confirm_password.length > 0 &&
                  resetPasswordData.confirm_password ===
                    resetPasswordData.new_password && (
                    <span className="mfg-password-strength mfg-strength-good">
                      <Check size={12} /> Passwords match
                    </span>
                  )}
              </div>
            </div>
            <div className="mfg-modal-footer">
              <button
                className="mfg-btn mfg-btn-outline"
                onClick={closeResetPasswordModal}
                disabled={isResetPasswordLoading}
              >
                Cancel
              </button>
              <button
                className="mfg-btn mfg-btn-primary mfg-btn-with-icon"
                onClick={handleResetPasswordSubmit}
                disabled={isResetPasswordLoading}
              >
                {isResetPasswordLoading ? (
                  <span className="mfg-loader-spinner"></span>
                ) : (
                  <>
                    <Check size={16} /> Save Password
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
